import type { ID, Region } from "@gooner/shared";

/** Fixed, dark-UI-legible hues dealt to sync groups in region order. */
const SYNC_TINTS = ["#7c3aed", "#0e7490", "#15803d", "#b45309", "#be185d", "#4338ca"];

/**
 * One colour per sync GROUP, keyed by region id, so linked tiles (and the link
 * menu's mentions of them) read as a set at a glance. A region belongs to the
 * group of its leader — its `syncWith`, or itself when another region follows it.
 * Unsynced regions get no entry. Colours cycle if a wall somehow has seven groups.
 */
export function syncTintMap(regions: readonly Region[]): Map<ID, string> {
  const followed = new Set(
    regions.map((r) => r.syncWith).filter((id): id is ID => !!id),
  );
  const groupOf = (r: Region): ID | null =>
    r.syncWith ?? (followed.has(r.id) ? r.id : null);
  const colors = new Map<ID, string>();
  const tints = new Map<ID, string>();
  for (const r of regions) {
    const g = groupOf(r);
    if (!g) continue;
    if (!colors.has(g)) colors.set(g, SYNC_TINTS[colors.size % SYNC_TINTS.length]);
    tints.set(r.id, colors.get(g)!);
  }
  return tints;
}

/**
 * Region id -> the ids of the OTHER members of its sync group, for the wall's
 * hover crosslight. Same membership rule as syncTintMap above — a region belongs
 * to its leader's group — kept beside it so the ring and the tint can never
 * disagree about who is linked. Unsynced regions get no entry.
 */
export function syncPeers(regions: readonly Region[]): Map<ID, ID[]> {
  const followed = new Set(
    regions.map((r) => r.syncWith).filter((id): id is ID => !!id),
  );
  const groupOf = (r: Region): ID | null =>
    r.syncWith ?? (followed.has(r.id) ? r.id : null);
  const members = new Map<ID, ID[]>();
  for (const r of regions) {
    const g = groupOf(r);
    if (!g) continue;
    let ids = members.get(g);
    if (!ids) members.set(g, (ids = []));
    ids.push(r.id);
  }
  const peers = new Map<ID, ID[]>();
  for (const ids of members.values()) {
    for (const id of ids) peers.set(id, ids.filter((x) => x !== id));
  }
  return peers;
}
